/**
 * Audio File Upload Hook
 *
 * Handles audio FILE uploads to Firebase Storage.
 * Similar to useAudioUpload but for File objects instead of recording Blobs.
 *
 * @module features/chat/hooks/chat-input/useAudioFileUpload
 */

import { useCallback } from "react";
import { toast } from "sonner";
import {
  isSupportedAudioType,
  validateAudioFile,
} from "@/constants";
import { uploadAudioToStorage } from "../../services/audio-upload-service";
import { createCaptureContentMessage } from "../../services/capture-service";
import { extractAudioDuration } from "@/lib/audio-utils";
import type { StorageAudioBlock, ContentBlock } from "../../types/messages";

// =============================================================================
// TYPES
// =============================================================================

export interface UseAudioFileUploadOptions {
  userId: string | undefined;
  conversationId: string | undefined;
  setContentBlocks: (
    blocks: ContentBlock[] | ((prev: ContentBlock[]) => ContentBlock[])
  ) => void;
  captureMode: boolean;
  savedBlockIdsRef: React.MutableRefObject<Set<string>>;
}

export interface UseAudioFileUploadReturn {
  uploadAudioFile: (file: File) => Promise<void>;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for uploading audio FILES to Firebase Storage.
 *
 * Features:
 * - Validates audio file type and size
 * - Extracts duration from audio file
 * - Uploads to Firebase Storage with progress tracking
 * - Creates storage audio blocks
 * - In capture mode: creates Firestore messages
 * - Shows error toasts on failure
 */
export function useAudioFileUpload({
  userId,
  conversationId,
  setContentBlocks,
  captureMode,
  savedBlockIdsRef,
}: UseAudioFileUploadOptions): UseAudioFileUploadReturn {
  const uploadAudioFile = useCallback(
    async (file: File) => {
      // Validate file type
      if (!isSupportedAudioType(file.type)) {
        toast.error("Ongeldig audioformaat");
        return;
      }

      // Validate file size
      const validation = validateAudioFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }

      if (!userId || !conversationId) {
        toast.error("Ontbrekende gebruiker of conversatie context");
        return;
      }

      // Extract duration
      let duration = 0;
      try {
        duration = await extractAudioDuration(file);
      } catch (error) {
        console.warn("Failed to extract audio duration:", error);
      }

      const tempGcsPath = `audio/temp-${Date.now()}-${file.name}`;

      // Add audio block immediately with uploading state
      const pendingAudioBlock: StorageAudioBlock = {
        type: "audio",
        source_type: "storage",
        mime_type: file.type,
        relative_gcs_path: tempGcsPath,
        fileName: file.name,
        metadata: {
          duration,
          name: file.name,
          size: file.size,
        },
        uploading: true,
        uploadProgress: 0,
      };

      console.log("📤 Adding pending audio file block:", file.name);
      setContentBlocks((prev) => [...prev, pendingAudioBlock]);

      try {
        const uploadResult = await uploadAudioToStorage(
          file, // File is a Blob subtype
          userId,
          conversationId,
          {
            duration,
            onProgress: (progress) => {
              setContentBlocks((prev) =>
                prev.map((block) =>
                  block.type === "audio" &&
                  block.source_type === "storage" &&
                  (block as StorageAudioBlock).relative_gcs_path === tempGcsPath
                    ? { ...block, uploadProgress: progress }
                    : block
                )
              );
            },
          }
        );

        console.log("✅ Audio file upload successful:", uploadResult);

        // Update block with actual storage path
        // Mark as creating Firestore message if in capture mode
        setContentBlocks((prev) =>
          prev.map((block) =>
            block.type === "audio" &&
            block.source_type === "storage" &&
            (block as StorageAudioBlock).relative_gcs_path === tempGcsPath
              ? {
                  ...block,
                  relative_gcs_path: uploadResult.storagePath,
                  fileName: uploadResult.fileName,
                  uploading: false,
                  uploadProgress: undefined,
                  creatingMessage: captureMode,
                }
              : block
          )
        );

        // In capture mode: create message in backend
        if (captureMode) {
          try {
            const response = await createCaptureContentMessage(
              userId,
              conversationId,
              {
                type: "audio",
                file_name: uploadResult.fileName,
                relative_gcs_path: uploadResult.storagePath,
                mime_type: file.type,
                metadata: { duration },
              }
            );

            setContentBlocks((prev) =>
              prev.map((b) => {
                if (
                  b.type === "audio" &&
                  b.source_type === "storage" &&
                  (b as StorageAudioBlock).relative_gcs_path ===
                    uploadResult.storagePath
                ) {
                  return { ...b, messageId: response.message_id, creatingMessage: undefined };
                }
                return b;
              })
            );

            savedBlockIdsRef.current.add(`${uploadResult.storagePath}-audio`);
            console.log(
              `[CaptureMode] ✅ Audio file message created: ${response.message_id}`
            );
          } catch (error) {
            console.error(
              "[CaptureMode] Failed to create audio file message:",
              error
            );
            // Clear creatingMessage flag on error so user can see it failed
            setContentBlocks((prev) =>
              prev.map((b) => {
                if (
                  b.type === "audio" &&
                  b.source_type === "storage" &&
                  (b as StorageAudioBlock).relative_gcs_path ===
                    uploadResult.storagePath
                ) {
                  return { ...b, creatingMessage: undefined };
                }
                return b;
              })
            );
            toast.error("Fout bij opslaan", {
              description: "Het audio bestand kon niet worden opgeslagen. Probeer het opnieuw.",
            });
          }
        }
      } catch (error) {
        console.error("❌ Failed to upload audio file:", error);
        // Remove the pending block on error
        setContentBlocks((prev) =>
          prev.filter(
            (block) =>
              !(
                block.type === "audio" &&
                block.source_type === "storage" &&
                (block as StorageAudioBlock).relative_gcs_path === tempGcsPath
              )
          )
        );
        toast.error("Audio bestand uploaden mislukt");
      }
    },
    [userId, conversationId, setContentBlocks, captureMode, savedBlockIdsRef]
  );

  return { uploadAudioFile };
}
